import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'

import LoadingScreen from '../components/LoadingScreen'
import { authAutoLogin, getAccessToken, logoutUser } from '../services/auth.service'

function PrivateRoute(): React.JSX.Element {
  const [loading, setLoading] = useState<boolean>(true)
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)

  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const token = getAccessToken()

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const autologinToken = urlParams.get('autologin')

    const performAutoLogin = async (token: string) => {
      try {
        const response = await authAutoLogin(token)
        if (response) {
          queryClient.invalidateQueries({ queryKey: ['user'] })

          const newUrl = new URL(window.location.href)
          newUrl.searchParams.delete('autologin')
          navigate(newUrl.pathname + newUrl.search, { replace: true })

          setIsLoggedIn(true)
        }
        else {
          setIsError(true)
        }
      }
      catch (error) {
        console.error('Auto-login failed:', error)
        setIsError(true)
      }
      finally {
        setLoading(false)
      }
    }

    if (autologinToken) {
      performAutoLogin(autologinToken)
    }
  }, [location.search, navigate, queryClient])

  useEffect(() => {
    const validateToken = async (token: string) => {
      try {
        const response = await fetch('/api/auth/validate', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        })

        if (response.ok) {
          setIsLoggedIn(true)
        }
        else if (response.status === 401) {
          logoutUser()
          setIsLoggedIn(false)
        }
        else {
          setIsError(true)
        }
      }
      catch (error) {
        console.error('Token validation failed:', error)
        setIsError(true)
      }
      finally {
        setLoading(false)
      }
    }

    if (!location.search.includes('autologin')) {
      if (token) {
        validateToken(token)
      }
      else {
        setLoading(false)
      }
    }
  }, [token, location.search])

  if (loading)
    return <LoadingScreen loadingText="" />
  if (isError)
    return <div>Fout! Er is een onbekende fout opgetreden. Probeer het later nog een keer.</div>
  if (!isLoggedIn)
    return <Navigate to="/login" replace />

  return <Outlet />
}

export default PrivateRoute
